import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { Loader2 } from '@/icons';
// import { ensureSupabaseInitialized, getSupabase } from '@/lib/supabaseClient.js';
import ErrorBoundary from '@/components/ErrorBoundary';

import Layout from '@/components/Layout';
import ProtectedRoute from '@/components/ProtectedRoute';
import AdminRoute from '@/components/AdminRoute';
import { AuthProvider } from '@/contexts/AuthContext';
import { ParentChildProvider } from '@/contexts/ParentChildContext.jsx';
import { ThemeProvider } from '@/contexts/ThemeContext.jsx';
import { LanguageProvider } from '@/contexts/LanguageContext';
import LanguageAuthSync from '@/components/LanguageAuthSync';
import UserStatusCheck from '@/components/UserStatusCheck';
import ScrollToTop from '@/components/ScrollToTop';
import DevelopmentHelpers from '@/components/DevelopmentHelpers';

const HomePage = lazy(() => import('@/pages/HomePage'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const CreditReport = lazy(() => import('@/pages/CreditReport'));
const DebtTracker = lazy(() => import('@/pages/DebtTracker'));
const GoalsPage = lazy(() => import('@/pages/GoalsPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/RegisterPage'));
const EmailConfirmedPage = lazy(() => import('@/pages/EmailConfirmedPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ProfilePage = lazy(() => import('@/pages/ProfilePage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const AboutPage = lazy(() => import('@/pages/footer/AboutPage'));
const PrivacyPage = lazy(() => import('@/pages/footer/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/footer/TermsPage'));
const ContactPage = lazy(() => import('@/pages/footer/ContactPage'));
const SupportPage = lazy(() => import('@/pages/footer/SupportPage'));
const SponsorsPage = lazy(() => import('@/pages/footer/SponsorsPage'));
const FeaturesPage = lazy(() => import('@/pages/footer/FeaturesPage'));
const WhyUrbanMintPage = lazy(() => import('@/pages/footer/WhyUrbanMintPage'));
const HelpRequestPage = lazy(() => import('@/pages/footer/HelpRequestPage'));
const GlossaryPage = lazy(() => import('@/pages/GlossaryPage'));
const LearnHubLayout = lazy(() => import('@/pages/learn_hub/LearnHubLayout'));
const GoodVsBadDebtPage = lazy(() => import('@/pages/learn_hub/GoodVsBadDebtPage'));
const IncomeStreamsPage = lazy(() => import('@/pages/learn_hub/IncomeStreamsPage'));
const InvestingBasicsPage = lazy(() => import('@/pages/learn_hub/InvestingBasicsPage'));
const PurchaseDecisionHelperPage = lazy(() => import('@/pages/learn_hub/PurchaseDecisionHelperPage'));
const ExamsListPage = lazy(() => import('@/pages/learn_hub/ExamsListPage'));
const ExamContainerWrapper = lazy(() => import('@/pages/exam/ExamContainerWrapper'));
const StartingBusinessPage = lazy(() => import('@/pages/learn_hub/StartingBusinessPage'));
const LearnBudgetingPage = lazy(() => import('@/pages/learn_hub/LearnBudgetingPage'));
const LearnSavingQuizPage = lazy(() => import('@/pages/learn_hub/LearnSavingQuizPage'));
const LearnInvestingVideoPage = lazy(() => import('@/pages/learn_hub/LearnInvestingVideoPage'));
const CreditScoreSimulatorPage = lazy(() => import('@/pages/learn_hub/CreditScoreSimulatorPage'));
const CreditBasicsPage = lazy(() => import('@/pages/learn_hub/CreditBasicsPage'));
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout'));
const AdminExamsPage = lazy(() => import('@/pages/admin/AdminExamsPage'));
const AdminMentorsPage = lazy(() => import('@/pages/admin/AdminMentorsPage'));
const AdminSettingsPage = lazy(() => import('@/pages/admin/AdminSettingsPage'));
const AdminResourcesPage = lazy(() => import('@/pages/admin/AdminResourcesPage'));
const AdminContentPagesPage = lazy(() => import('@/pages/admin/AdminContentPagesPage'));
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage.jsx'));
const AdminTermsPage = lazy(() => import('@/pages/admin/AdminTermsPage'));
const NetWorthPage = lazy(() => import('@/pages/NetWorthPage'));
const RewardsPage = lazy(() => import('@/pages/RewardsPage'));
const SpendingTrackerPage = lazy(() => import('@/pages/SpendingTrackerPage'));
const FindMentorPage = lazy(() => import('@/pages/mentors/FindMentorPage'));
const BecomeMentorPage = lazy(() => import('@/pages/mentors/BecomeMentorPage'));
const MentorProfilePage = lazy(() => import('@/pages/mentors/MentorProfilePage'));
const MentorUpdatePage = lazy(() => import('@/pages/mentor_update/MentorUpdatePage'));
const FreeResourcesPage = lazy(() => import('@/pages/resources/FreeResourcesPage'));
const EarnPage = lazy(() => import('@/pages/EarnPage'));
const ScenarioListPage = lazy(() => import('@/pages/scenarios/ScenarioListPage'));
const ScenarioPlayerPage = lazy(() => import('@/pages/scenarios/ScenarioPlayerPage'));
const DynamicContentPage = lazy(() => import('@/pages/DynamicContentPage'));
const ManageChildrenPage = lazy(() => import('@/pages/parent/ManageChildrenPage.jsx'));

const TourProvider = lazy(() => {
  return import('@/contexts/TourContext.jsx');
});

const SuspenseFallback = () => (
  <div className="flex justify-center items-center h-screen w-screen fixed inset-0 bg-transparent z-[200]">
    <Loader2 className="w-12 h-12 animate-spin text-primary" />
  </div>
);

const AppContent = () => {
  return (
    <>
      <ScrollToTop />
      {process.env.NODE_ENV === 'development' && <DevelopmentHelpers />}
      <Routes>
        <Route element={<Layout />}> 
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/email-confirmed" element={<EmailConfirmedPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/mentor-update/:token" element={<MentorUpdatePage />} />
          <Route path="/free-resources" element={<FreeResourcesPage />} />
          <Route path="/earn" element={<EarnPage />} />
          <Route path="/glossary" element={<GlossaryPage />} />

          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/sponsors" element={<SponsorsPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/why-urbanmint" element={<WhyUrbanMintPage />} /> 
          <Route path="/help-request" element={<ProtectedRoute><HelpRequestPage /></ProtectedRoute>} />

          <Route path="/learn" element={<LearnHubLayout />}>
            <Route index element={<Navigate to="budgeting" replace />} />
            <Route path="budgeting" element={<LearnBudgetingPage />} />
            <Route path="good-vs-bad-debt" element={<GoodVsBadDebtPage />} />
            <Route path="credit-basics" element={<CreditBasicsPage />} />
            <Route path="income-streams" element={<IncomeStreamsPage />} />
            <Route path="investing-basics" element={<InvestingBasicsPage />} />
            <Route path="decision-helper" element={<PurchaseDecisionHelperPage />} />
            <Route path="exams" element={<ProtectedRoute><ExamsListPage /></ProtectedRoute>} />
            <Route path="exam/:examId" element={<ProtectedRoute><ExamContainerWrapper /></ProtectedRoute>} />
            <Route path="starting-business" element={<StartingBusinessPage />} />
            <Route path="saving-quiz" element={<LearnSavingQuizPage />} />
            <Route path="investing-video" element={<LearnInvestingVideoPage />} />
            <Route path="score-simulator" element={<CreditScoreSimulatorPage />} />
          </Route>

          <Route path="/scenarios" element={<ProtectedRoute><ScenarioListPage /></ProtectedRoute>} />
          <Route path="/scenarios/:scenarioId" element={<ProtectedRoute><ScenarioPlayerPage /></ProtectedRoute>} />

          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/credit-report" element={<ProtectedRoute><CreditReport /></ProtectedRoute>} />
          <Route path="/debt-tracker" element={<ProtectedRoute><DebtTracker /></ProtectedRoute>} />
          <Route path="/goals" element={<ProtectedRoute><GoalsPage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/net-worth" element={<ProtectedRoute><NetWorthPage /></ProtectedRoute>} />
          <Route path="/rewards" element={<ProtectedRoute><RewardsPage /></ProtectedRoute>} />
          <Route path="/spending-tracker" element={<ProtectedRoute><SpendingTrackerPage /></ProtectedRoute>} />
          <Route path="/parent/children" element={<ProtectedRoute><ManageChildrenPage /></ProtectedRoute>} />

          <Route path="/mentors" element={<FindMentorPage />} />
          <Route path="/mentors/:mentorId" element={<MentorProfilePage />} />
          <Route path="/become-mentor" element={<BecomeMentorPage />} />

          <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
            <Route index element={<Navigate to="users" replace />} /> 
            <Route path="users" element={<AdminUsersPage />} /> 
            <Route path="mentors" element={<AdminMentorsPage />} />
            <Route path="exams" element={<AdminExamsPage />} />
            <Route path="resources" element={<AdminResourcesPage />} />
            <Route path="pages" element={<AdminContentPagesPage />} /> 
            <Route path="terms" element={<AdminTermsPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
          </Route>
          
          <Route path="/p/:slug" element={<DynamicContentPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      <Toaster />
    </>
  );
};

function App() {
  const [forceRender, setForceRender] = React.useState(false);
  
  React.useEffect(() => {
    // Force render after 3 seconds to prevent infinite loading
    const timeoutId = setTimeout(() => {
      setForceRender(true);
    }, 3000);
    
    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <LanguageAuthSync />
            <UserStatusCheck>
              <ParentChildProvider>
                <Suspense fallback={<SuspenseFallback />}>
                  <TourProvider>
                    <div className="min-h-screen bg-background">
                      <AppContent />
                    </div>
                  </TourProvider>
                </Suspense>
              </ParentChildProvider>
            </UserStatusCheck>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
  
